import { useCallback, useContext, useState } from 'react';
import { OkButton } from 'src/components/Buttons/OkButton';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { useListingCanBeWasted } from 'src/hooks/api/useListingCanBeWasted';
import { useEventItemLoadingDisplay } from 'src/hooks/useEventItemLoadingDisplay';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { CancellableFormFooter } from 'src/modals/common';
import { CancellableFormHeader } from 'src/modals/common/CancellableFormHeader';
import { ConnectedEventEntityHeader } from 'src/modals/common/EventEntityHeader';
import { modalDetails } from 'src/modals/common/Modals.css';
import { Summary } from 'src/modals/common/Summary';
import { SaleWasteInfo } from 'src/modals/ListingDetails/components/WasteUnsoldTickets/SaleWasteInfo';
import { ModalBody, ModalFooter, ModalProps } from 'src/modals/Modal';
import { ModalBodyHeaderContainer } from 'src/modals/Modal/Modal.styled';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { isSuccess } from 'src/utils/errorUtils';
import { EntityWithRealTickets } from 'src/utils/ticketUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  ApiException,
  Feature,
  ListingDetails,
  SaleClient,
  SaleDetails,
} from 'src/WebApiController';

import * as styles from './WasteUnsoldTickets.css';
import { SaleDateOption } from './WasteUnsoldTickets.types';

type WasteUnsoldTicketsProps = {
  cancelTo?: ModalProps;
  onWastedSuccess: (saleId: number) => void;
};

const getDefaultSaleDateOption = (
  eventDate: string | null | undefined
): SaleDateOption => {
  if (!eventDate) {
    return SaleDateOption.CurrentTimestamp;
  }
  const now = new Date();
  const eventTime = new Date(eventDate);
  return now < eventTime
    ? SaleDateOption.CurrentTimestamp
    : SaleDateOption.TimeOfEvent;
};

export const WasteUnsoldTickets = ({
  cancelTo,
  onWastedSuccess,
}: WasteUnsoldTicketsProps) => {
  const { posEntity, event, posEntityDisplayId, posEntityId } =
    useActivePosEntityContext<EntityWithRealTickets>();
  const listing =
    posEntity?.entityType === ActionOutboxEntityType.Listing
      ? (posEntity as ListingDetails)
      : undefined;
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const hasChooseDateFeature = useUserHasFeature(Feature.WastageSaleChooseDate);

  const eventDateUtc = event?.datesUtc?.start ?? event?.dates.start;

  const [saleDateOption, setSaleDateOption] = useState<SaleDateOption>(
    getDefaultSaleDateOption(eventDateUtc)
  );

  const canBeWastedQuery = useListingCanBeWasted(listing?.id);
  const { item1: canBeWasted, item2: ticketsToWaste } =
    canBeWastedQuery.data ?? {};

  const { showErrorDialog, genericError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { setModal, closeModal } = useContext(ModalContext);

  const { isLoading: entityIsLoading, loadingState } =
    useEventItemLoadingDisplay<SaleDetails>(
      FormatContentId.LoadingSaleId,
      FormatContentId.SearchingForSaleId,
      FormatContentId.CouldNotFindSaleId,
      posEntityDisplayId ?? posEntityId?.toString()
    );

  const getSaleDateValue = useCallback((): string | null => {
    if (!hasChooseDateFeature) {
      return null;
    }
    if (saleDateOption === SaleDateOption.CurrentTimestamp) {
      return new Date().toISOString();
    }
    return eventDateUtc ?? null;
  }, [hasChooseDateFeature, saleDateOption, eventDateUtc]);

  const onSubmit = useCallback(async () => {
    setIsLoading(true);
    tryInvokeApi(
      async () => {
        if (!listing || !canBeWasted) {
          return null;
        }
        const client = new SaleClient(activeAccountWebClientConfig);

        const r = await client.wasteListingTicketsByListingId(listing.id, {
          listingId: listing.id,
          saleDate: getSaleDateValue(),
        });

        if (isSuccess(r) && r.entityId) {
          onWastedSuccess(r.entityId);
          if (cancelTo) {
            setModal(cancelTo);
          } else {
            closeModal(true);
          }
        } else {
          showErrorDialog(
            'SaleClient.mergePosManagedMarketplaceSale',
            {
              message: r.message ?? genericError,
              status: r.status!,
            } as ApiException,
            {
              trackErrorData: {
                listing: listing?.id,
              },
            }
          );
        }
      },
      (error) => {
        showErrorDialog('SaleClient.mergePosManagedMarketplaceSale', error, {
          trackErrorData: {
            listing: listing?.id,
          },
        });
      },
      () => {
        setIsLoading(false);
      }
    );
  }, [
    activeAccountWebClientConfig,
    canBeWasted,
    cancelTo,
    closeModal,
    genericError,
    listing,
    onWastedSuccess,
    getSaleDateValue,
    setModal,
    showErrorDialog,
  ]);

  return (
    <>
      <CancellableFormHeader
        cancelTo={cancelTo}
        disabled={isLoading}
        showDialogOnCancel={true}
      >
        <ConnectedEventEntityHeader
          title={
            <>
              <Content id={ContentId.WasteSale} />
            </>
          }
        />
      </CancellableFormHeader>

      <ModalBody>
        <div className={modalDetails}>
          {event && listing && (
            <ModalBodyHeaderContainer>
              <Summary event={event} posEntity={listing} />
            </ModalBodyHeaderContainer>
          )}
          {entityIsLoading ||
          !listing ||
          !event ||
          isLoading ||
          canBeWastedQuery.isFetching ? (
            <PosSpinner />
          ) : (
            <div className={styles.content}>
              <SaleWasteInfo
                eventDate={event.dates.start}
                seating={listing.seating}
                tickets={ticketsToWaste}
                canBeWasted={!!canBeWasted}
                hasChooseDateFeature={hasChooseDateFeature}
                saleDateOption={saleDateOption}
                onSaleDateOptionChange={setSaleDateOption}
              />
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <CancellableFormFooter
          cancelTo={cancelTo}
          disabled={isLoading || entityIsLoading}
          showDialogOnCancel={true}
        >
          <OkButton
            data-testid="confirm-waste-button"
            onClick={onSubmit}
            disabled={isLoading || entityIsLoading || !canBeWasted}
            textContentId={ContentId.ConfirmWaste}
          />
        </CancellableFormFooter>
      </ModalFooter>
    </>
  );
};
